/// <amd-module name="Test/TestCaseResults/Component/ResultsTableComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { getResultsToRender, getUniqueFilterValuesToRender } from "Redux/TestCaseResultsSelector";
import {
    IResultsTableContainer,
    ITestCaseResultsFilterSelection,
    TestCaseResultsItemProvider
} from "Test/TestCaseResults/Component/TestCaseResultsItemProvider";
import * as Constants from "Test/TestCaseResults/Constants";
import { TestCaseResultsActions } from "Test/TestCaseResults/Redux/TestCaseResultsActions";
import { ITestCaseResultsAwareState, ITestCaseResultsFilterValues } from "Test/TestCaseResults/Redux/TestCaseResultsContracts";
import { TestCaseResultsModule } from "Test/TestCaseResults/Redux/TestCaseResultsModule";
import * as Resources from "Test/TestCaseResults/Resources";
import { IResultsPageOpenMenu } from "Test/TestCaseResults/Services/TestCaseResultsMenuService";
import { ITestCaseResultsFilterItem } from "Test/TestCaseResultsFilter/TestCaseResultsFilterComponent.Types";
import { compareColumnWidthsMaps } from "Test/TestPlansHubRefresh/TestHub/Util";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { IReadonlyObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";
import { CustomCard } from "VSSUI/Card";
import { IListSelection, ListSelection } from "VSSUI/List";
import { IMenuItem } from "VSSUI/Menu";
import { ColumnMore, ITableColumn, renderSimpleCell, Table } from "VSSUI/Table";
import { ZeroData } from "VSSUI/ZeroData";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IQueryParameter, Uri } from "VSS/Platform/Util/Url";
import * as Utils_String from "VSS/Core/Util/String";

export interface IResultsTableComponentProps extends IVssComponentProperties {
    contextTestPoint: Contracts.TestPointDetailedReference;

    testCaseResults: Contracts.TestCaseAssociatedResult[];

    filterValues: ITestCaseResultsFilterValues;

    // map storing column id alongwith their width
    columnAndWidthMap: { [columnId: string]: number };

    // method to declare TTI
    markTTI(featureName: string, scenarioName: string, additionalProperties?: any): void;

    // opens the results page
    openResultsPage(pointId: number, runId: number, resultId: number): void;
}

/**
 * ResultsTableComponent: Renders the table containing test case results
 */
export class ResultsTableComponent extends VssComponent<IResultsTableComponentProps> {
    private _items: IResultsTableContainer;
    private _resultsTableSelection: IListSelection;
    private _provider: TestCaseResultsItemProvider;
    private _filterSelection: ITestCaseResultsFilterSelection;
    private _tableColumns: ITableColumn<any>[];
    private _columnId: string;
    private _columnWidth: number;

    constructor(props: IResultsTableComponentProps) {
        super(props);
        this._resultsTableSelection = new ListSelection(true);
        this._provider = new TestCaseResultsItemProvider();
        this._tableColumns = this._getColumns();
        this._filterSelection = {
            outcome: new ListSelection(true),
            tester: new ListSelection(true),
            configuration: new ListSelection(true),
            plan: new ListSelection(true),
            suite: new ListSelection(true)
        } as ITestCaseResultsFilterSelection;

        this._items = this._provider.getTestCaseResultsForTable(props.testCaseResults);
    }

    public componentDidMount() {
        const url = new Uri(window.location.href);
        const queryParams = url.queryParameters;
        const params: { [param: string]: string } = {};
        if (queryParams && queryParams.length > 0) {
            queryParams.forEach((param: IQueryParameter) => {
                if (param.name && param.value) {
                    params[param.name] = param.value;
                }
            });
        }
        /**
         * show notification only on the first render if test point doesnot exist and contextPointId is
         * present in the query params. Do no need to show notification in case when params only has testCaseId
         * Eg: When test result retention period is over, test point will not exist
        */
        if(!this.props.contextTestPoint && params[Constants.QueryParamContextPointId]){
            this._showToastNotifications(Utils_String.format(Resources.TestPointNotExist, params[Constants.QueryParamContextPointId]), Constants.ToastNotificationDurationInMs);
        }
    }

    public shouldComponentUpdate(nextProps: IResultsTableComponentProps) {
        if (!compareColumnWidthsMaps(this.props.columnAndWidthMap, nextProps.columnAndWidthMap)) {
            this._tableColumns = this._getColumns(nextProps.columnAndWidthMap);
        }
        this._items = this._provider.getTestCaseResultsForTable(nextProps.testCaseResults);

        return true;
    }

    public componentDidUpdate() {
        this._markTTIPerf();
    }

    public render(): JSX.Element {
        let card;

        if (this._items && this._items.resultsTableItem.length > 0) {
            card = (
                <Table
                    className="testcase-results-testcase-table"
                    containerClassName="custom-scrollbar"
                    columns={this._tableColumns}
                    scrollable={true}
                    itemProvider={this._items.resultsTableItem}
                    onActivate={this._openResultsPage}
                    singleClickActivation={false}
                    selection={this._resultsTableSelection}
                />
            );
        } else {
            const image = getAssetLocation(this.context.pageContext, Constants.ZeroDataImagePath);

            card = (
                <>
                    <ZeroData
                        imagePath={image}
                        className="testcase-results-filter-zero-data"
                        imageAltText={Resources.NoFilteredResultsFound}
                        primaryText={Resources.NoFilteredResultsFound}
                        secondaryText={Resources.NoFilteredResultsFoundSecondary}
                    />
                </>
            );
        }
        return (
            <DynamicModuleLoader modules={[TestCaseResultsModule]}>
                <div className="testcase-results-table-container flex-grow">
                    <WrappedComponent
                        dependencies={["ms.vss-test-web.testcase-results-filter-component"]}
                        wrappedType={"testcase-results-filter-component"}
                        className={"testcase-results-filter-bar"}
                        filterBarItems={this._getFilterBarItems()}
                    />
                    <CustomCard
                        className="testcase-results-card bolt-card-with-header"
                        headerClassName="bolt-card-header bolt-header-with-commandbar"
                    >
                        {card}
                    </CustomCard>
                </div>
            </DynamicModuleLoader>
        );
    }

    private _markTTIPerf() {
        // This is a page load scenario, use helper action to declare TTI
        this.props.markTTI(Constants.TEST_HUB_FEATURE_AREA_NAME, Constants.TESTCASE_RESULTS_FEATURE_AREA_NAME, {
            ttiScenario: "PageLoad"
        });
    }

    /**
     * _getFilterBarItems: Returns the filter bar items and the values to render within them in case of dropdown filter item
     */
    private _getFilterBarItems(): ITestCaseResultsFilterItem[] {
        let filterBarItems: ITestCaseResultsFilterItem[];

        const outcomeFilterItem: ITestCaseResultsFilterItem = {
            key: "outcome-filter",
            filterItemKey: Constants.OutcomeFilterKey,
            placeholder: Resources.OutcomeColumnHeader,
            items: this.props.filterValues.outcome,
            selection: this._filterSelection.outcome,
            width: 200
        };

        const configurationFilterItem: ITestCaseResultsFilterItem = {
            key: "configuration-filter",
            filterItemKey: Constants.ConfigurationFilterKey,
            placeholder: Resources.ConfigurationColumnHeader,
            items: this.props.filterValues.configuration,
            selection: this._filterSelection.configuration,
            width: 200
        };

        const testerFilterItem: ITestCaseResultsFilterItem = {
            key: "tester-filter",
            filterItemKey: Constants.TesterFilterKey,
            placeholder: Resources.TesterColumnHeader,
            items: this.props.filterValues.tester,
            selection: this._filterSelection.tester,
            width: 200
        };

        const planFilterItem: ITestCaseResultsFilterItem = {
            key: "plan-filter",
            filterItemKey: Constants.PlanFilterKey,
            placeholder: Resources.TestPlanColumnHeader,
            items: this.props.filterValues.plan,
            selection: this._filterSelection.plan,
            width: 200
        };

        const suiteFilterItem: ITestCaseResultsFilterItem = {
            key: "suite-filter",
            filterItemKey: Constants.SuiteFilterKey,
            placeholder: Resources.TestSuiteColumnHeader,
            items: this.props.filterValues.suite,
            selection: this._filterSelection.suite,
            width: 200
        };

        filterBarItems = [outcomeFilterItem, configurationFilterItem, testerFilterItem, planFilterItem, suiteFilterItem];

        return filterBarItems;
    }

    private _openResultsPage = (): void => {
        const testCaseIndex = this._resultsTableSelection.value[0].beginIndex;

        const pointId: number = this._items.metadata.value[testCaseIndex].pointId;
        const runId: number = this._items.metadata.value[testCaseIndex].runId;
        const resultId: number = this._items.metadata.value[testCaseIndex].resultId;
        this.props.openResultsPage(pointId, runId, resultId);
    };

    private _getTestCaseResultsContextMenuItemsFromProvider = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        const props: IResultsPageOpenMenu = {
            runDetail: {
                pointId: this.props.testCaseResults[this._resultsTableSelection.value[0].beginIndex].pointId,
                runId: this.props.testCaseResults[this._resultsTableSelection.value[0].beginIndex].runId,
                resultId: this.props.testCaseResults[this._resultsTableSelection.value[0].beginIndex].resultId
            },
            dispatch: this.props.dispatch
        };
        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.testcase-results-menu", props));
        return menuItems;
    };

    private _showToastNotifications = (message: string, duration: number): void => {
        let messageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messageService.addToast({
            duration: duration,
            message: message
        });
    }

    private _onColumnSize = (event: MouseEvent, columnIndex: number, width: number) => {
        const originalColumnWidth = this._tableColumns[columnIndex].width;
        (originalColumnWidth as ObservableValue<number>).value = width;
        this._columnId = this._tableColumns[columnIndex].id;
        this._columnWidth = width;
    };

    private _columnSizeEnd = () => {
        this.props.changeColumnWidths(this._columnId, this._columnWidth);
    };

    private _getWidth(
        defaultWidth: number,
        columnId: string,
        columnAndWidthMap: { [columnId: string]: number } | undefined
    ): ObservableValue<number> {
        return columnAndWidthMap && columnAndWidthMap[columnId]
            ? new ObservableValue(columnAndWidthMap[columnId])
            : new ObservableValue(defaultWidth);
    }

    private _getColumns(columnAndWidthMap?: { [columnId: string]: number }): ITableColumn<any>[] {
        const tableColumns: ITableColumn<any>[] = [];
        const menuItemColumn = new ColumnMore(
            () => {
                return {
                    id: "MenuColumn",
                    items: this._getTestCaseResultsContextMenuItemsFromProvider()
                };
            },
            undefined,
            (rowindex: number, columnIndex: number) => {
                if (!this._resultsTableSelection.selected(rowindex)) {
                    this._resultsTableSelection.select(rowindex, 1, false);
                }
            }
        );

        tableColumns.push(
            {
                id: Constants.OutcomeColumn,
                name: Resources.OutcomeColumnHeader,
                width:  this._getWidth(150, Constants.OutcomeColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            },
            menuItemColumn,
            {
                id: Constants.TimestampColumn,
                name: Resources.TimeStampColumnHeader,
                width: this._getWidth(150, Constants.TimestampColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.ConfigurationColumn,
                name: Resources.ConfigurationColumnHeader,
                width: this._getWidth(200, Constants.ConfigurationColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.RunbyColumn,
                name: Resources.RunByColumnHeader,
                width: this._getWidth(220, Constants.RunbyColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.TesterColumn,
                name: Resources.TesterColumnHeader,
                width: this._getWidth(220, Constants.TesterColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.TestPlanColumn,
                name: Resources.TestPlanColumnHeader,
                width: this._getWidth(200, Constants.TestPlanColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.TestSuiteColumn,
                name: Resources.TestSuiteColumnHeader,
                width: this._getWidth(200, Constants.TestSuiteColumn, columnAndWidthMap),
                onSize: this._onColumnSize,
                onSizeEnd: this._columnSizeEnd,
                renderCell: renderSimpleCell
            }
        );

        return tableColumns;
    }
}

function mapStateToProps(state: ITestCaseResultsAwareState): Partial<IResultsTableComponentProps> {
    const filterValuesWrapper = getUniqueFilterValuesToRender(state);

    return {
        contextTestPoint: state.testCaseResultsState.contextPoint,
        testCaseResults: getResultsToRender(state),
        filterValues: {
            outcome: filterValuesWrapper[Constants.OutcomeFilterKey],
            tester: filterValuesWrapper[Constants.TesterFilterKey],
            configuration: filterValuesWrapper[Constants.ConfigurationFilterKey],
            plan: filterValuesWrapper[Constants.PlanFilterKey],
            suite: filterValuesWrapper[Constants.SuiteFilterKey]
        },
        columnAndWidthMap: state.testCaseResultsState.columnAndWidthMap
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<IResultsTableComponentProps> {
    return {
        markTTI: (featureName: string, scenarioName: string, additionalProperties?: any) =>
            dispatch(TTIMonitorActions.markTTI(featureName, scenarioName, additionalProperties)),
        openResultsPage: (pointId: number, runId: number, resultId: number) => dispatch(TestCaseResultsActions.openRunsHub(pointId, runId, resultId)),
        changeColumnWidths: (columnId: string, width: number) => dispatch(TestCaseResultsActions.changeColumnWidths(columnId, width)),
        dispatch
    };
}

/**
 * ResultsTableFunctionalComponent: This functional component is just a dumb wrapper over ResultsTableComponent.
 * We use this because React.useContext can only be invoked in function components. We get the context here and then pass is as props to the class component.
 */
export function ResultsTableFunctionalComponent(props: IResultsTableComponentProps): JSX.Element {
    return <ResultsTableComponent {...props} />;
}

export const ResultsTableConnectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps
)(ResultsTableFunctionalComponent);
